import { Link } from "react-router-dom";
import { useDocumentHead } from "../../lib/document-head.ts";
import { Nav } from "../home/components/Nav.tsx";
import { SiteFooter } from "../home/components/SiteFooter.tsx";
import { useHomeMotion } from "../home/hooks/useHomeMotion.ts";
import { IconArrowRight } from "../home/components/icons.tsx";
import { hostBenefits, HOST_EMAIL, MAILTO_HOST } from "../home/data.ts";

const steps = [
  {
    title: "Tell us about your event",
    body: "Send us the basics — what you're running, when and where, and roughly how many people you expect.",
  },
  {
    title: "We set you up",
    body: "We build your event page, entry form and check-in, and tune it to how your day actually runs.",
  },
  {
    title: "Go live & capture leads",
    body: "Share your link, scan people in at the gate, and keep every contact long after the event ends.",
  },
];

/**
 * /host — the organizer-facing marketing page. The "Host an Event" CTAs across the site land here
 * (rather than firing a bare mailto): organizers get the pitch and the details first, then a
 * deliberate contact action. The email is the contact mechanism for the pilot until the organizer
 * dashboard/signup flow exists.
 */
export function HostScreen() {
  const { scrolled } = useHomeMotion();

  useDocumentHead({
    title: "Host an Event — RestoMag Events",
    description:
      "Run your automotive, aviation or marine event with RestoMag — register attendees, capture leads, check people in, and grow your community. Get in touch to get started.",
    robots: "index",
  });

  return (
    <div className="flex min-h-screen flex-col bg-ink font-archivo text-white">
      <Nav scrolled={scrolled} />
      <main className="flex-1 pt-[calc(4rem_+_var(--ann-bar-h,0px))]">
        {/* Hero */}
        <section className="border-b border-white/[.07] px-5 py-16 md:px-8 md:py-24 lg:px-12 lg:py-28">
          <div className="mx-auto max-w-shell">
            <div className="mb-5 font-mono text-xs uppercase tracking-[.28em] text-flame">For organizers</div>
            <h1 className="m-0 max-w-[16ch] font-archivo text-[clamp(38px,6vw,80px)] font-extrabold leading-[1] tracking-[-.03em]">
              Host your event with RestoMag.
            </h1>
            <p className="mt-6 max-w-[56ch] text-lg leading-[1.6] text-white/70">
              From a Sunday Cars &amp; Coffee to a three-day airshow — register attendees, capture leads, check people
              in and grow your community. The tools fade into the background. The event takes the stage.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-3.5">
              <a
                href={MAILTO_HOST}
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-flame bg-flame px-[30px] py-4 text-base font-bold text-white no-underline transition hover:-translate-y-0.5 hover:border-flame-dark hover:bg-flame-dark sm:w-auto"
              >
                Email us to get started <IconArrowRight size={17} />
              </a>
              <Link
                to="/"
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-white/[.24] bg-white/[.06] px-7 py-4 text-base font-semibold text-white no-underline backdrop-blur-[8px] transition hover:bg-white/[.14] sm:w-auto"
              >
                Back to events
              </Link>
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="px-5 py-16 md:px-8 md:py-24 lg:px-12">
          <div className="mx-auto max-w-shell">
            <h2 className="m-0 mb-12 font-archivo text-[clamp(28px,3.4vw,48px)] font-extrabold leading-[1.05] tracking-[-.025em]">
              Everything you need to run the day.
            </h2>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
              {hostBenefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] px-[22px] py-6 transition hover:-translate-y-[3px] hover:border-flame"
                >
                  <benefit.icon size={22} className="mb-4 text-flame" />
                  <div className="mb-1.5 text-base font-bold">{benefit.title}</div>
                  <div className="text-[13.5px] leading-[1.5] text-white/55">{benefit.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="border-t border-white/[.07] bg-black px-5 py-16 md:px-8 md:py-24 lg:px-12">
          <div className="mx-auto max-w-shell">
            <h2 className="m-0 mb-12 font-archivo text-[clamp(28px,3.4vw,48px)] font-extrabold leading-[1.05] tracking-[-.025em]">
              How it works.
            </h2>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {steps.map((step, i) => (
                <div key={step.title}>
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-flame/[.12] font-mono text-base font-bold text-flame">
                    {i + 1}
                  </div>
                  <div className="mb-2 text-lg font-bold">{step.title}</div>
                  <div className="text-[14.5px] leading-[1.6] text-white/55">{step.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="px-5 py-16 md:px-8 md:py-24 lg:px-12">
          <div className="mx-auto flex max-w-shell flex-col items-start gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:flex-row md:items-center md:justify-between md:p-12">
            <div>
              <h2 className="m-0 text-2xl font-extrabold tracking-tight sm:text-3xl">Ready to host?</h2>
              <p className="mt-2 text-white/60">
                Tell us about your event and we'll get you set up. Email{" "}
                <span className="font-semibold text-white">{HOST_EMAIL}</span>.
              </p>
            </div>
            <a
              href={MAILTO_HOST}
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-flame bg-flame px-[30px] py-4 text-base font-bold text-white no-underline transition hover:-translate-y-0.5 hover:border-flame-dark hover:bg-flame-dark md:w-auto md:shrink-0"
            >
              Email us to get started <IconArrowRight size={17} />
            </a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
